import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { FormattedMessage } from 'react-intl'
import { useSelector, useDispatch } from 'react-redux'
import { Grid, InputLabel } from '@mui/material'
import { CloudUpload } from '@mui/icons-material'
import UploadedImage from 'components/form/UploadedImage'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import ImageCropDialog from 'components/dialog/ImageCropDialog'
import { selectChallenge, clearFileUploadStatus } from 'store/challenge/slice'

interface Props {
  name: string
  register?: (instance: any) => void
  label?: string
  buttonText: string
  suggestedWidth: number
  suggestedHeight: number
  allowedFileTypes?: string
  imagePosition?: 'bottom' | 'right'
  s3Url: string
  fileType: string
  uploadFile?: (file: File, fileType: string) => void
  deleteFile?: () => void
  disabled?: boolean
  circularImage?: boolean
  acceptFileType?: string
  showDescription?: boolean
}

export default function FileUpload({
  name,
  register,
  label,
  buttonText,
  suggestedWidth,
  suggestedHeight,
  allowedFileTypes,
  imagePosition = 'right',
  s3Url,
  fileType,
  uploadFile,
  deleteFile,
  disabled = false,
  circularImage = false,
  acceptFileType = 'image/*',
  showDescription = true,
}: Props): JSX.Element {
  const imageGridSize = imagePosition === 'bottom' ? 12 : 5
  const [fileIsUploading, setFileIsUploading] = useState<boolean>(false)
  const [imageSrc, setImageSrc] = useState<string>('')
  const [imageType, setImageType] = useState<string>('image/png')
  const [cropOpen, setCropOpen] = useState<boolean>(false)
  const dispatch = useDispatch()
  const { fileUploadStatus } = useSelector(selectChallenge)

  useEffect(() => {
    const hideFileUploading = ['failed', 'succeeded'].includes(fileUploadStatus)
    if (hideFileUploading && fileIsUploading) {
      setFileIsUploading(false)
    }
  }, [fileUploadStatus, fileIsUploading])

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target
    if (files && files.length > 0) {
      const file = files[0]
      const isImageFile = file?.type.includes('image')

      if (isImageFile) {
        const image = URL.createObjectURL(file)
        setImageSrc(image)
        setImageType(file.type)
        setCropOpen(true)
      } else {
        handleUpload(file)
      }
    }
  }

  const handleCropClose = () => {
    setCropOpen(false)
  }

  const handleUpload = (file: File) => {
    if (file && uploadFile) {
      dispatch(clearFileUploadStatus())
      setFileIsUploading(true)
      uploadFile(file, fileType)
    }
  }

  const openFileInput = () => {
    if (!disabled) {
      document.getElementById(name)?.click()
    }
  }

  const UploadButton = disabled ? DisabledUploadButton : ActiveUploadButton

  return (
    <Grid container spacing={0}>
      {label && (
        <Grid item xs={12}>
          <StyledLabel htmlFor={name}>{label}</StyledLabel>
        </Grid>
      )}

      {!disabled && (
        <Grid item xs={imageGridSize}>
          <input
            style={{ display: 'none' }}
            name={name}
            {...(register ? register(name) : {})}
            id={name}
            type="file"
            accept={acceptFileType}
            onChange={handleChange}
            onClick={() => null}
            disabled={disabled}
          />

          <UploadButton type="button" onClick={openFileInput}>
            <CloudUpload />
            {fileIsUploading ? <LoadingSpinnerGreen /> : <span>{buttonText}</span>}
          </UploadButton>

          {showDescription && (
            <Description>
              {suggestedWidth && (
                <>
                  <FormattedMessage id="files.suggestedSize" />
                  {': '}
                  {suggestedWidth}x{suggestedHeight}px
                  <br />
                </>
              )}
              {allowedFileTypes && (
                <>
                  <FormattedMessage id="files.allowedFileTypes" />
                  {': '}
                  {allowedFileTypes}
                </>
              )}
            </Description>
          )}

          <ImageCropDialog
            name={name}
            open={cropOpen}
            src={imageSrc}
            imageType={imageType}
            handleUpload={handleUpload}
            handleClose={handleCropClose}
            width={suggestedWidth}
            height={suggestedHeight}
            circularImage={circularImage}
          />
        </Grid>
      )}

      <Grid item xs={imageGridSize}>
        <UploadedImage imageUrl={`${s3Url}`} circularImage={circularImage} deleteFile={deleteFile} />
      </Grid>
    </Grid>
  )
}

const StyledLabel = styled(InputLabel)`
  margin-bottom: 15px;
`

const ActiveUploadButton = styled.button`
  background-color: #fff;
  color: ${(props) => props.theme.palette.primary.main};
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  border-radius: 5px;
  height: 32px;
  min-width: 120px;
  cursor: pointer;

  & span {
    font-weight: 600;
    position: relative;
    top: -0.2em;
  }

  & svg {
    margin-right: 5px;
    font-size: 1.2em;
  }
`

const DisabledUploadButton = styled(ActiveUploadButton)`
  background-color: ${(props) => props.theme.customPalette.lightGray};
  color: ${(props) => props.theme.customPalette.mediumGray};
  border-color: ${(props) => props.theme.customPalette.mediumGray};
  cursor: default;
`

const Description = styled.div`
  margin-top: 10px;
  font-size: 0.75em;
  line-height: 1.6em;
`
